import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, Dimensions } from 'react-native';
import {
  Text,
  Card,
  useTheme,
  Chip,
  IconButton,
  Button,
} from 'react-native-paper';
import { LineChart, BarChart, PieChart } from '../common/Charts';
import type { MasteryState, ProgressInsight } from '@coffeeplexity/shared';

const { width: screenWidth } = Dimensions.get('window');
const chartWidth = screenWidth - 64;

export interface LearningInsight {
  id: string;
  type: 'trend' | 'pattern' | 'recommendation' | 'achievement' | 'challenge';
  title: string;
  description: string;
  confidence: number;
  impact: 'high' | 'medium' | 'low';
  actionable: boolean;
  data?: any;
  generatedAt: string;
}

export interface ProgressAnalytics {
  userId: string;
  timeRange: '7d' | '30d' | '90d' | '1y';
  overallProgress: {
    currentLevel: number;
    progressRate: number;
    streakDays: number;
    totalSkills: number;
    masteredSkills: number;
  };
  skillProgress: {
    skillId: string;
    skillName: string;
    category: string;
    currentMastery: number;
    progressHistory: { date: string; value: number }[];
    trend: 'improving' | 'stable' | 'declining';
    velocity: number;
  }[];
  learningPatterns: {
    optimalLearningTimes: string[];
    sessionDuration: number;
    contentPreferences: { type: string; engagement: number }[];
    difficultyPreference: number;
  };
  insights: LearningInsight[];
  predictions: {
    nextMilestone: { skill: string; estimatedDate: string; confidence: number };
    skillMastery: { skillId: string; estimatedCompletion: string }[];
    learningEfficiency: number;
  };
}

export interface ProgressAnalyticsDashboardProps {
  analytics: ProgressAnalytics;
  onTimeRangeChange: (range: '7d' | '30d' | '90d' | '1y') => void;
  onInsightAction?: (insight: LearningInsight) => void;
  onSkillSelect?: (skillId: string) => void;
  style?: any;
}

export function ProgressAnalyticsDashboard({
  analytics,
  onTimeRangeChange,
  onInsightAction,
  onSkillSelect,
  style,
}: ProgressAnalyticsDashboardProps) {
  const theme = useTheme();
  const [selectedTab, setSelectedTab] = useState<
    'overview' | 'skills' | 'patterns' | 'insights'
  >('overview');

  const getProgressColor = (progress: number) => {
    if (progress >= 0.8) return '#4CAF50';
    if (progress >= 0.6) return '#8BC34A';
    if (progress >= 0.4) return '#FFC107';
    if (progress >= 0.2) return '#FF9800';
    return '#F44336';
  };

  const getInsightIcon = (type: LearningInsight['type']) => {
    switch (type) {
      case 'trend':
        return 'trending-up';
      case 'pattern':
        return 'chart-line';
      case 'recommendation':
        return 'lightbulb';
      case 'achievement':
        return 'trophy';
      case 'challenge':
        return 'alert-circle';
      default:
        return 'information';
    }
  };

  const getInsightColor = (impact: LearningInsight['impact']) => {
    switch (impact) {
      case 'high':
        return '#F44336';
      case 'medium':
        return '#FF9800';
      case 'low':
        return '#4CAF50';
      default:
        return '#9E9E9E';
    }
  };

  const renderOverviewTab = () => {
    const progressData = [
      { label: 'Mon', value: 65 },
      { label: 'Tue', value: 68 },
      { label: 'Wed', value: 72 },
      { label: 'Thu', value: 70 },
      { label: 'Fri', value: 75 },
      { label: 'Sat', value: 78 },
      { label: 'Sun', value: 82 },
    ];

    return (
      <ScrollView style={styles.tabContent}>
        {/* Overall Stats */}
        <Card style={styles.statsCard} mode='outlined'>
          <Card.Content>
            <Text variant='titleMedium' style={styles.cardTitle}>
              Overall Progress
            </Text>

            <View style={styles.statsGrid}>
              <View style={styles.statItem}>
                <Text
                  variant='headlineMedium'
                  style={[styles.statValue, { color: '#4CAF50' }]}
                >
                  {Math.round(analytics.overallProgress.currentLevel * 100)}%
                </Text>
                <Text variant='bodySmall' style={styles.statLabel}>
                  Current Level
                </Text>
              </View>

              <View style={styles.statItem}>
                <Text
                  variant='headlineMedium'
                  style={[styles.statValue, { color: '#FF9800' }]}
                >
                  {analytics.overallProgress.streakDays}
                </Text>
                <Text variant='bodySmall' style={styles.statLabel}>
                  Day Streak
                </Text>
              </View>

              <View style={styles.statItem}>
                <Text
                  variant='headlineMedium'
                  style={[styles.statValue, { color: '#2196F3' }]}
                >
                  {analytics.overallProgress.masteredSkills}/
                  {analytics.overallProgress.totalSkills}
                </Text>
                <Text variant='bodySmall' style={styles.statLabel}>
                  Skills Mastered
                </Text>
              </View>

              <View style={styles.statItem}>
                <Text
                  variant='headlineMedium'
                  style={[styles.statValue, { color: '#9C27B0' }]}
                >
                  +{Math.round(analytics.overallProgress.progressRate * 100)}%
                </Text>
                <Text variant='bodySmall' style={styles.statLabel}>
                  Weekly Growth
                </Text>
              </View>
            </View>
          </Card.Content>
        </Card>

        {/* Progress Chart */}
        <Card style={styles.chartCard} mode='outlined'>
          <Card.Content>
            <Text variant='titleMedium' style={styles.cardTitle}>
              Progress Trend
            </Text>

            <LineChart
              data={progressData}
              width={chartWidth}
              height={220}
              color='#4CAF50'
            />
          </Card.Content>
        </Card>

        {/* Next Milestone */}
        {analytics.predictions.nextMilestone && (
          <Card style={styles.milestoneCard} mode='outlined'>
            <Card.Content>
              <View style={styles.milestoneHeader}>
                <Text variant='titleMedium' style={styles.cardTitle}>
                  Next Milestone
                </Text>
                <Chip
                  icon='target'
                  mode='outlined'
                  compact
                  style={styles.confidenceChip}
                >
                  {Math.round(
                    analytics.predictions.nextMilestone.confidence * 100
                  )}
                  % confident
                </Chip>
              </View>

              <Text variant='bodyLarge' style={styles.milestoneSkill}>
                {analytics.predictions.nextMilestone.skill}
              </Text>

              <Text variant='bodyMedium' style={styles.milestoneDate}>
                Estimated completion:{' '}
                {new Date(
                  analytics.predictions.nextMilestone.estimatedDate
                ).toLocaleDateString()}
              </Text>
            </Card.Content>
          </Card>
        )}
      </ScrollView>
    );
  };

  const renderSkillsTab = () => {
    const skillData = analytics.skillProgress
      .slice(0, 5)
      .map(skill => ({
        label: skill.skillName.substring(0, 8),
        value: skill.currentMastery * 100,
      }));

    return (
      <ScrollView style={styles.tabContent}>
        {/* Skills Chart */}
        <Card style={styles.chartCard} mode='outlined'>
          <Card.Content>
            <Text variant='titleMedium' style={styles.cardTitle}>
              Skill Mastery Levels
            </Text>

            <BarChart
              data={skillData}
              width={chartWidth}
              height={220}
              color='#2196F3'
            />
          </Card.Content>
        </Card>

        {/* Skills List */}
        {analytics.skillProgress.map(skill => (
          <Card key={skill.skillId} style={styles.skillCard} mode='outlined'>
            <Card.Content>
              <View style={styles.skillHeader}>
                <View style={styles.skillInfo}>
                  <Text variant='titleSmall' style={styles.skillName}>
                    {skill.skillName}
                  </Text>
                  <Text variant='bodySmall' style={styles.skillCategory}>
                    {skill.category}
                  </Text>
                </View>

                <View style={styles.skillStats}>
                  <Text
                    variant='headlineSmall'
                    style={[
                      styles.skillMastery,
                      { color: getProgressColor(skill.currentMastery) },
                    ]}
                  >
                    {Math.round(skill.currentMastery * 100)}%
                  </Text>

                  <Chip
                    icon={
                      skill.trend === 'improving'
                        ? 'trending-up'
                        : skill.trend === 'declining'
                          ? 'trending-down'
                          : 'trending-neutral'
                    }
                    mode='outlined'
                    compact
                    style={[
                      styles.trendChip,
                      {
                        borderColor:
                          skill.trend === 'improving'
                            ? '#4CAF50'
                            : skill.trend === 'declining'
                              ? '#F44336'
                              : '#9E9E9E',
                      },
                    ]}
                    textStyle={{
                      color:
                        skill.trend === 'improving'
                          ? '#4CAF50'
                          : skill.trend === 'declining'
                            ? '#F44336'
                            : '#9E9E9E',
                    }}
                  >
                    {skill.trend}
                  </Chip>
                </View>
              </View>

              <View style={styles.progressBar}>
                <View
                  style={[
                    styles.progressTrack,
                    { backgroundColor: theme.colors.surfaceVariant },
                  ]}
                />
                <View
                  style={[
                    styles.progressFill,
                    {
                      width: `${skill.currentMastery * 100}%`,
                      backgroundColor: getProgressColor(skill.currentMastery),
                    },
                  ]}
                />
              </View>

              {onSkillSelect && (
                <Button
                  mode='text'
                  onPress={() => onSkillSelect(skill.skillId)}
                  style={styles.skillButton}
                >
                  View Details
                </Button>
              )}
            </Card.Content>
          </Card>
        ))}
      </ScrollView>
    );
  };

  const renderPatternsTab = () => {
    const contentData = analytics.learningPatterns.contentPreferences.map(
      pref => ({
        label: pref.type,
        value: pref.engagement,
      })
    );

    return (
      <ScrollView style={styles.tabContent}>
        {/* Learning Patterns */}
        <Card style={styles.patternsCard} mode='outlined'>
          <Card.Content>
            <Text variant='titleMedium' style={styles.cardTitle}>
              Learning Patterns
            </Text>

            <View style={styles.patternItem}>
              <Text variant='bodyMedium' style={styles.patternLabel}>
                Optimal Learning Times
              </Text>
              <View style={styles.timeChips}>
                {analytics.learningPatterns.optimalLearningTimes.map(
                  (time, index) => (
                    <Chip
                      key={index}
                      mode='outlined'
                      compact
                      style={styles.timeChip}
                    >
                      {time}
                    </Chip>
                  )
                )}
              </View>
            </View>

            <View style={styles.patternItem}>
              <Text variant='bodyMedium' style={styles.patternLabel}>
                Average Session Duration
              </Text>
              <Text variant='headlineSmall' style={styles.patternValue}>
                {analytics.learningPatterns.sessionDuration} minutes
              </Text>
            </View>

            <View style={styles.patternItem}>
              <Text variant='bodyMedium' style={styles.patternLabel}>
                Difficulty Preference
              </Text>
              <View style={styles.difficultyBar}>
                <View
                  style={[
                    styles.difficultyFill,
                    {
                      width: `${analytics.learningPatterns.difficultyPreference * 100}%`,
                      backgroundColor: '#2196F3',
                    },
                  ]}
                />
              </View>
              <Text variant='bodySmall' style={styles.difficultyLabel}>
                {analytics.learningPatterns.difficultyPreference < 0.3
                  ? 'Beginner'
                  : analytics.learningPatterns.difficultyPreference < 0.7
                    ? 'Intermediate'
                    : 'Advanced'}
              </Text>
            </View>
          </Card.Content>
        </Card>

        {/* Content Preferences Chart */}
        <Card style={styles.chartCard} mode='outlined'>
          <Card.Content>
            <Text variant='titleMedium' style={styles.cardTitle}>
              Content Engagement
            </Text>

            <BarChart
              data={contentData}
              width={chartWidth}
              height={220}
              color='#9C27B0'
            />
          </Card.Content>
        </Card>
      </ScrollView>
    );
  };

  const renderInsightsTab = () => {
    return (
      <ScrollView style={styles.tabContent}>
        {analytics.insights.map(insight => (
          <Card key={insight.id} style={styles.insightCard} mode='outlined'>
            <Card.Content>
              <View style={styles.insightHeader}>
                <View style={styles.insightHeaderLeft}>
                  <IconButton
                    icon={getInsightIcon(insight.type)}
                    size={20}
                    iconColor={getInsightColor(insight.impact)}
                    style={[
                      styles.insightIcon,
                      {
                        backgroundColor: getInsightColor(insight.impact) + '20',
                      },
                    ]}
                  />

                  <View style={styles.insightTitleContainer}>
                    <Text variant='titleSmall' style={styles.insightTitle}>
                      {insight.title}
                    </Text>
                    <View style={styles.insightMeta}>
                      <Chip
                        mode='outlined'
                        compact
                        style={[
                          styles.impactChip,
                          { borderColor: getInsightColor(insight.impact) },
                        ]}
                        textStyle={{ color: getInsightColor(insight.impact) }}
                      >
                        {insight.impact} impact
                      </Chip>

                      <Chip
                        mode='outlined'
                        compact
                        style={styles.confidenceChip}
                      >
                        {Math.round(insight.confidence * 100)}% confident
                      </Chip>
                    </View>
                  </View>
                </View>
              </View>

              <Text variant='bodyMedium' style={styles.insightDescription}>
                {insight.description}
              </Text>

              {insight.actionable && onInsightAction && (
                <Button
                  mode='contained'
                  onPress={() => onInsightAction(insight)}
                  style={styles.insightAction}
                  compact
                >
                  Take Action
                </Button>
              )}
            </Card.Content>
          </Card>
        ))}
      </ScrollView>
    );
  };

  return (
    <View style={[styles.container, style]}>
      {/* Header */}
      <View style={styles.header}>
        <Text variant='headlineSmall' style={styles.title}>
          Progress Analytics
        </Text>

        {/* Time Range Selector */}
        <View style={styles.timeRangeSelector}>
          {(['7d', '30d', '90d', '1y'] as const).map(range => (
            <Chip
              key={range}
              mode={analytics.timeRange === range ? 'flat' : 'outlined'}
              onPress={() => onTimeRangeChange(range)}
              style={styles.timeRangeChip}
              compact
            >
              {range}
            </Chip>
          ))}
        </View>
      </View>

      {/* Tab Navigation */}
      <View style={styles.tabNavigation}>
        {(['overview', 'skills', 'patterns', 'insights'] as const).map(tab => (
          <Button
            key={tab}
            mode={selectedTab === tab ? 'contained' : 'outlined'}
            onPress={() => setSelectedTab(tab)}
            style={styles.tabButton}
            compact
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </Button>
        ))}
      </View>

      {/* Tab Content */}
      {selectedTab === 'overview' && renderOverviewTab()}
      {selectedTab === 'skills' && renderSkillsTab()}
      {selectedTab === 'patterns' && renderPatternsTab()}
      {selectedTab === 'insights' && renderInsightsTab()}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  header: {
    padding: 16,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  title: {
    fontWeight: '600',
    marginBottom: 12,
  },
  timeRangeSelector: {
    flexDirection: 'row',
    gap: 8,
  },
  timeRangeChip: {
    backgroundColor: 'transparent',
  },
  tabNavigation: {
    flexDirection: 'row',
    padding: 16,
    backgroundColor: '#fff',
    gap: 8,
  },
  tabButton: {
    flex: 1,
  },
  tabContent: {
    flex: 1,
    padding: 16,
  },
  statsCard: {
    marginBottom: 16,
  },
  cardTitle: {
    fontWeight: '600',
    marginBottom: 16,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  statItem: {
    alignItems: 'center',
    width: '48%',
    marginBottom: 16,
  },
  statValue: {
    fontWeight: '700',
    marginBottom: 4,
  },
  statLabel: {
    opacity: 0.7,
    textAlign: 'center',
  },
  chartCard: {
    marginBottom: 16,
  },
  chart: {
    marginVertical: 8,
    borderRadius: 16,
  },
  milestoneCard: {
    marginBottom: 16,
  },
  milestoneHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  confidenceChip: {
    backgroundColor: 'transparent',
  },
  milestoneSkill: {
    fontWeight: '600',
    marginBottom: 8,
    color: '#1976D2',
  },
  milestoneDate: {
    opacity: 0.8,
  },
  skillCard: {
    marginBottom: 12,
  },
  skillHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  skillInfo: {
    flex: 1,
  },
  skillName: {
    fontWeight: '600',
    marginBottom: 4,
  },
  skillCategory: {
    opacity: 0.7,
  },
  skillStats: {
    alignItems: 'flex-end',
  },
  skillMastery: {
    fontWeight: '700',
    marginBottom: 4,
  },
  trendChip: {
    backgroundColor: 'transparent',
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    position: 'relative',
    marginBottom: 12,
  },
  progressTrack: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 4,
  },
  progressFill: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    borderRadius: 4,
  },
  skillButton: {
    alignSelf: 'flex-start',
  },
  patternsCard: {
    marginBottom: 16,
  },
  patternItem: {
    marginBottom: 16,
  },
  patternLabel: {
    fontWeight: '500',
    marginBottom: 8,
  },
  timeChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  timeChip: {
    backgroundColor: 'transparent',
  },
  patternValue: {
    fontWeight: '600',
    color: '#1976D2',
  },
  difficultyBar: {
    height: 8,
    backgroundColor: '#e0e0e0',
    borderRadius: 4,
    marginBottom: 8,
  },
  difficultyFill: {
    height: '100%',
    borderRadius: 4,
  },
  difficultyLabel: {
    opacity: 0.7,
  },
  insightCard: {
    marginBottom: 12,
  },
  insightHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  insightHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    flex: 1,
  },
  insightIcon: {
    margin: 0,
    marginRight: 12,
  },
  insightTitleContainer: {
    flex: 1,
  },
  insightTitle: {
    fontWeight: '600',
    marginBottom: 8,
  },
  insightMeta: {
    flexDirection: 'row',
    gap: 8,
  },
  impactChip: {
    backgroundColor: 'transparent',
  },
  insightDescription: {
    lineHeight: 20,
    marginBottom: 12,
  },
  insightAction: {
    alignSelf: 'flex-start',
  },
});
